/**
 * Microphone Assistant
 * A user-friendly system to help with microphone issues and provide fallback options
 */

class MicrophoneAssistant {
    constructor() {
        this.container = null;
        this.textButton = null;
        this.helpButton = null;
        this.hasTriedMicAccess = false;
        this.micStatus = 'unknown'; // 'unknown', 'available', 'unavailable'
        this.autoHideTimeout = null; // Track the auto-hide timeout
        this.isTextModeEnabled = false; // Track if user chose text mode
        
        this.init();
    }
    
    init() {
        // Create the assistant once DOM is loaded
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.createAssistant());
        } else {
            this.createAssistant();
        }
    }
    
    createAssistant() {
        // Create a subtle assistant that appears when needed
        this.container = document.createElement('div');
        this.container.className = 'mic-assistant-container';
        this.container.style.display = 'none';
        
        // Create main message area
        const messageArea = document.createElement('div');
        messageArea.className = 'mic-assistant-message';
        messageArea.innerHTML = 'Having trouble with your microphone?';
        
        // Create button container
        const buttonContainer = document.createElement('div');
        buttonContainer.className = 'mic-assistant-buttons';
        
        // Create text mode button
        this.textButton = document.createElement('button');
        this.textButton.className = 'mic-assistant-button text-mode-btn';
        this.textButton.innerHTML = '<i class="fas fa-keyboard"></i> Use Text Mode';
        this.textButton.addEventListener('click', () => this.enableTextMode());
        
        // Create help button
        this.helpButton = document.createElement('button');
        this.helpButton.className = 'mic-assistant-button help-btn';
        this.helpButton.innerHTML = '<i class="fas fa-question-circle"></i> Microphone Help';
        this.helpButton.addEventListener('click', () => this.showMicrophoneHelp());
        
        // Create dismiss button (will be shown after multiple attempts)
        this.dismissButton = document.createElement('button');
        this.dismissButton.className = 'mic-assistant-button dismiss-btn';
        this.dismissButton.innerHTML = '<i class="fas fa-times"></i> Don\'t Show Again';
        this.dismissButton.addEventListener('click', () => this.dismissPermanently());
        this.dismissButton.style.display = 'none'; // Hidden by default
        
        // Add buttons to container
        buttonContainer.appendChild(this.textButton);
        buttonContainer.appendChild(this.helpButton);
        buttonContainer.appendChild(this.dismissButton);
        
        // Add elements to main container
        this.container.appendChild(messageArea);
        this.container.appendChild(buttonContainer);
        
        // Add to document
        document.body.appendChild(this.container);
        
        // Add styles
        this.addStyles();
        
        // Try to detect microphone status
        this.checkMicrophoneStatus();
    }
    
    addStyles() {
        const styleElement = document.createElement('style');
        styleElement.textContent = `
            .mic-assistant-container {
                position: fixed;
                bottom: 210px;
                left: 50%;
                transform: translateX(-50%);
                background: rgba(0, 0, 0, 0.8);
                border-radius: 10px;
                padding: 15px;
                z-index: 1000;
                color: white;
                text-align: center;
                backdrop-filter: blur(5px);
                border: 1px solid rgba(255, 255, 255, 0.2);
                box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
                max-width: 90%;
                width: 400px;
                opacity: 0;
                transition: opacity 0.3s ease, transform 0.3s ease;
                transform: translateX(-50%) translateY(20px);
            }
            
            .mic-assistant-container.visible {
                opacity: 1;
                transform: translateX(-50%) translateY(0);
            }
            
            .mic-assistant-message {
                margin-bottom: 12px;
                font-size: 16px;
            }
            
            .mic-assistant-buttons {
                display: flex;
                justify-content: center;
                gap: 10px;
            }
            
            .mic-assistant-button {
                background: transparent;
                border: 1px solid rgba(255, 255, 255, 0.3);
                color: white;
                padding: 8px 15px;
                border-radius: 20px;
                cursor: pointer;
                transition: all 0.2s ease;
                font-size: 14px;
                display: flex;
                align-items: center;
                gap: 5px;
            }
            
            .mic-assistant-button:hover {
                background: rgba(255, 255, 255, 0.1);
                border-color: rgba(255, 255, 255, 0.5);
            }
            
            .mic-assistant-button.text-mode-btn {
                background: rgba(66, 133, 244, 0.2);
            }
            
            .mic-assistant-button.text-mode-btn:hover {
                background: rgba(66, 133, 244, 0.4);
            }
            
            .mic-assistant-button.help-btn {
                background: rgba(52, 168, 83, 0.2);
            }
            
            .mic-assistant-button.help-btn:hover {
                background: rgba(52, 168, 83, 0.4);
            }
            
            .mic-help-overlay {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: rgba(0, 0, 0, 0.8);
                z-index: 2000;
                display: flex;
                align-items: center;
                justify-content: center;
                opacity: 0;
                transition: opacity 0.3s ease;
                pointer-events: none;
            }
            
            .mic-help-overlay.visible {
                opacity: 1;
                pointer-events: auto;
            }
            
            .mic-help-content {
                background: #222;
                border-radius: 10px;
                padding: 25px;
                width: 90%;
                max-width: 500px;
                color: white;
                box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
                position: relative;
                max-height: 80vh;
                overflow-y: auto;
            }
            
            .mic-help-close {
                position: absolute;
                top: 15px;
                right: 15px;
                background: transparent;
                border: none;
                color: rgba(255, 255, 255, 0.7);
                font-size: 24px;
                cursor: pointer;
                width: 30px;
                height: 30px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 50%;
            }
            
            .mic-help-close:hover {
                background: rgba(255, 255, 255, 0.1);
                color: white;
            }
            
            .mic-help-content h2 {
                margin-top: 0;
                color: #4285f4;
                font-size: 20px;
                margin-bottom: 20px;
            }
            
            .mic-help-step {
                margin-bottom: 15px;
                display: flex;
                align-items: flex-start;
                gap: 10px;
            }
            
            .mic-help-step-number {
                background: #4285f4;
                color: white;
                width: 25px;
                height: 25px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: bold;
                flex-shrink: 0;
            }
            
            .mic-help-step-text {
                text-align: left;
                line-height: 1.5;
            }
            
            .mic-try-again-btn {
                background: #4285f4;
                color: white;
                border: none;
                padding: 12px 20px;
                border-radius: 25px;
                margin-top: 15px;
                cursor: pointer;
                font-weight: bold;
                transition: background 0.2s ease;
            }
            
            .mic-try-again-btn:hover {
                background: #3367d6;
            }
        `;
        document.head.appendChild(styleElement);
    }
    
    checkMicrophoneStatus() {
        // Skip if we've already determined status
        if (this.micStatus !== 'unknown') return;
        
        // For initialization, just check capabilities but don't show UI
        // The assistant will only be shown if user actually tries to use the mic
        
        // Check if browser supports microphone access
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            this.micStatus = 'unavailable';
            return;
        }
        
        // NOTE (Task #530 rework): SpeechRecognition support is deliberately
        // NOT checked here. Transcription runs through ElevenLabs Scribe
        // realtime on the shared getUserMedia stream — browsers without SR
        // (e.g. Firefox) have fully working voice. Marking them
        // 'unavailable' would wrongly block startCall/startDictation.
        
        // Enumerate devices to see if microphone exists
        navigator.mediaDevices.enumerateDevices()
            .then(devices => {
                const hasMicrophone = devices.some(device => device.kind === 'audioinput');
                
                if (!hasMicrophone) {
                    this.micStatus = 'unavailable';
                } else {
                    // Microphone exists, don't try to access it yet
                    // We'll only access it when user explicitly uses the mic
                    this.micStatus = 'available';
                }
            })
            .catch(err => {
                console.error('Error enumerating devices:', err);
                // If we can't enumerate devices, assume mic is unavailable
                this.micStatus = 'unavailable';
            });
    }
    
    tryMicrophoneAccess() {
        // Allow retrying microphone access on each attempt
        // Remove the single-try limitation to support repeated attempts
        console.log('Attempting microphone access...');
        
        navigator.mediaDevices.getUserMedia({ audio: true })
            .then(stream => {
                // Microphone works!
                stream.getTracks().forEach(track => track.stop());
                this.micStatus = 'available';
                
                // Try speech recognition as final test
                this.trySpeechRecognition();
            })
            .catch(err => {
                console.error('Microphone access error:', err);
                this.micStatus = 'unavailable';
                this.showAssistant();
            });
    }
    
    trySpeechRecognition() {
        try {
            const recognition = new (window.webkitSpeechRecognition || window.SpeechRecognition)();
            recognition.continuous = false;
            
            recognition.onerror = (event) => {
                if (event.error === 'not-allowed' || event.error === 'no-speech' || 
                    event.error === 'network' || event.error === 'service-not-allowed') {
                    this.micStatus = 'unavailable';
                    this.showAssistant();
                }
            };
            
            recognition.onend = () => {
                // Do nothing, this is just a test
            };
            
            recognition.start();
            
            // Stop after a short period
            setTimeout(() => {
                try {
                    recognition.stop();
                } catch (e) {
                    // Ignore errors when stopping
                }
            }, 2000);
        } catch (e) {
            console.error('Speech recognition error:', e);
            this.micStatus = 'unavailable';
            this.showAssistant();
        }
    }
    
    showAssistant() {
        if (!this.container) return;
        
        console.log('Showing microphone assistant for device issue');
        
        // Clear any existing auto-hide timeout
        if (this.autoHideTimeout) {
            clearTimeout(this.autoHideTimeout);
            this.autoHideTimeout = null;
        }
        
        this.container.style.display = 'block';
        // Trigger layout before adding visible class for animation
        void this.container.offsetWidth;
        this.container.classList.add('visible');
        
        // Auto-hide after 10 seconds only if text mode hasn't been enabled
        this.autoHideTimeout = setTimeout(() => {
            if (!this.isTextModeEnabled) {
                this.hideAssistant();
            }
        }, 10000);
    }
    
    // Method to force show assistant on microphone button clicks
    forceShowOnMicClick() {
        console.log('Force showing microphone assistant due to microphone button click with persistent issues');
        
        // Always show the assistant when explicitly called from mic button click
        if (!this.container) return;
        
        // Clear any existing auto-hide timeout
        if (this.autoHideTimeout) {
            clearTimeout(this.autoHideTimeout);
            this.autoHideTimeout = null;
        }
        
        this.container.style.display = 'block';
        // Trigger layout before adding visible class for animation
        void this.container.offsetWidth;
        this.container.classList.add('visible');
        
        // Auto-hide after 15 seconds for forced display
        this.autoHideTimeout = setTimeout(() => {
            this.hideAssistant();
        }, 15000);
    }
    
    hideAssistant() {
        if (!this.container) return;
        
        // Clear any auto-hide timeout
        if (this.autoHideTimeout) {
            clearTimeout(this.autoHideTimeout);
            this.autoHideTimeout = null;
        }
        
        this.container.classList.remove('visible');
        setTimeout(() => {
            this.container.style.display = 'none';
        }, 300);
    }
    
    enableTextMode() {
        console.log('Text mode enabled - permanently hiding microphone assistant');
        
        // Mark text mode as enabled to prevent re-showing
        this.isTextModeEnabled = true;
        
        // Stop any active microphone listening + the listening UI animation.
        // The user opted out of voice input, so the pulsing mic button must
        // not keep running. stopListening() also clears micButton.active and
        // micButtonContainer.listening classes.
        try {
            window.chatController?.stopListening?.();
        } catch (e) {
            console.warn('enableTextMode: stopListening failed', e);
        }
        // Defensive: also clear the listening visuals directly in case the
        // chat controller isn't initialized yet.
        document.querySelectorAll('.mic-button, #mic-button').forEach((btn) => {
            btn.classList.remove('active', 'listening', 'recording');
        });
        document.querySelectorAll('.mic-button-container, #mic-button-container').forEach((c) => {
            c.classList.remove('listening', 'active', 'recording');
        });
        
        // Find chat container and make it visible
        const chatContainer = document.querySelector('.chat-container');
        if (chatContainer) {
            chatContainer.classList.add('visible');
        }
        
        // Focus on the input field — but NEVER on touch/mobile, where an
        // auto-focus pops the on-screen keyboard before the user taps. On
        // touch devices the keyboard should appear only after an explicit tap
        // on the field itself.
        const chatInput = document.getElementById('chat-input');
        if (chatInput) {
            const isTouch = (typeof window.matchMedia === 'function'
                && window.matchMedia('(pointer: coarse)').matches)
                || ('ontouchstart' in window && navigator.maxTouchPoints > 0);
            if (!isTouch) {
                chatInput.focus();
            }
        }
        
        // Hide the assistant permanently
        this.hideAssistant();
    }
    
    dismissPermanently() {
        console.log('User permanently dismissed microphone assistant');
        
        // Mark as permanently dismissed
        this.isTextModeEnabled = true;
        
        // Store preference in localStorage to persist across sessions
        try {
            localStorage.setItem('micAssistantDismissed', 'true');
        } catch (e) {
            console.warn('Could not save microphone assistant preference:', e);
        }
        
        // Hide the assistant
        this.hideAssistant();
    }
    
    showMicrophoneHelp() {
        // Create help overlay if it doesn't exist
        if (!document.querySelector('.mic-help-overlay')) {
            const overlay = document.createElement('div');
            overlay.className = 'mic-help-overlay';
            
            const content = document.createElement('div');
            content.className = 'mic-help-content';
            
            const closeBtn = document.createElement('button');
            closeBtn.className = 'mic-help-close';
            closeBtn.innerHTML = '×';
            closeBtn.addEventListener('click', () => {
                overlay.classList.remove('visible');
                setTimeout(() => {
                    overlay.remove();
                }, 300);
            });
            
            content.innerHTML = `
                <h2>Microphone Troubleshooting</h2>
                
                <div class="mic-help-step">
                    <div class="mic-help-step-number">1</div>
                    <div class="mic-help-step-text">
                        Check if your microphone is properly connected and not muted on your device.
                    </div>
                </div>
                
                <div class="mic-help-step">
                    <div class="mic-help-step-number">2</div>
                    <div class="mic-help-step-text">
                        Make sure you've allowed microphone access in your browser:
                        <ul>
                            <li>Look for the padlock icon in your address bar</li>
                            <li>Click it and make sure microphone access is set to "Allow"</li>
                        </ul>
                    </div>
                </div>
                
                <div class="mic-help-step">
                    <div class="mic-help-step-number">3</div>
                    <div class="mic-help-step-text">
                        Check if other applications are using your microphone. Close any apps that might be using it.
                    </div>
                </div>
                
                <div class="mic-help-step">
                    <div class="mic-help-step-number">4</div>
                    <div class="mic-help-step-text">
                        If using a headset or external mic, try unplugging and plugging it back in.
                    </div>
                </div>
                
                <div class="mic-help-step">
                    <div class="mic-help-step-number">5</div>
                    <div class="mic-help-step-text">
                        Try refreshing the page after making any changes.
                    </div>
                </div>
                
                <button class="mic-try-again-btn">Try Microphone Again</button>
            `;
            
            content.appendChild(closeBtn);
            overlay.appendChild(content);
            document.body.appendChild(overlay);
            
            // Add event listener to try again button. The old behaviour
            // only ran an internal getUserMedia probe with no visible
            // feedback. Instead, close the overlay, show an inline
            // "Checking microphone…" status on the button briefly, then
            // trigger the real mic activation path the user expects —
            // which is exactly what happens when they click the mic
            // button in the toolbar.
            const tryAgainBtn = overlay.querySelector('.mic-try-again-btn');
            tryAgainBtn.addEventListener('click', async () => {
                const originalLabel = tryAgainBtn.textContent;
                tryAgainBtn.disabled = true;
                tryAgainBtn.textContent = 'Checking microphone…';

                this.micStatus = 'unknown';

                // Reset persistent dismissal state so the user is no
                // longer locked out of voice input after retrying.
                this.isTextModeEnabled = false;
                try { localStorage.removeItem('micAssistantDismissed'); } catch (_) {}

                let success = false;
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    stream.getTracks().forEach((t) => t.stop());
                    this.micStatus = 'available';
                    success = true;
                } catch (err) {
                    console.warn('Try Microphone Again: getUserMedia failed', err);
                    this.micStatus = 'unavailable';
                }

                if (success) {
                    // Close the help overlay first so the user sees the app.
                    overlay.classList.remove('visible');
                    setTimeout(() => overlay.remove(), 300);
                    this.hideAssistant();

                    // Trigger the canonical "start mic" flow by clicking
                    // the actual mic button. Falls back to a direct
                    // controller call if the button isn't in the DOM yet.
                    const micBtn = document.getElementById('mic-button')
                        || document.querySelector('.mic-button');
                    if (micBtn) {
                        micBtn.click();
                    } else if (typeof window.chatController?.startListening === 'function') {
                        try { window.chatController.startListening(); } catch (_) {}
                    }
                } else {
                    // Give the user a clear "still not working" signal and
                    // leave the overlay open so they can re-read the steps.
                    tryAgainBtn.textContent = 'Still no microphone — try the steps above';
                    setTimeout(() => {
                        tryAgainBtn.textContent = originalLabel;
                        tryAgainBtn.disabled = false;
                    }, 2500);
                }
            });
            
            // Show overlay with animation
            setTimeout(() => {
                overlay.classList.add('visible');
            }, 10);
        }
    }
}

// Initialize the assistant
const micAssistant = new MicrophoneAssistant();

// Expose globally for other scripts to access
window.micAssistant = micAssistant;